<template>
  <div class="bg-white dark:bg-neutral-900">
    <PageBanner
      :src="!!data || !!error ? banner : undefined"
      alt="user banner"
    />
    <div class="px-4 lg:px-8 relative pb-4">
      <div class="header-grid">
        <UserAvatar
          class="header-grid-avatar rounded-full"
          :class="{ 'ring-none': user?.relationships.find(isFrame) }"
          :user="user || undefined"
          no-link
          skeleton
          auto-size
          res="512"
        />
        <div class="header-grid-options flex items-start justify-end space-x-2">
          <NamiButton
            v-if="authUser?.profile.sub !== user?.id"
            button-type="primary"
            text
            title="Message"
            :disabled="loading || !user"
            @button-click="goToSendMessage"
          >
            <IconMail :size="22.8" />
          </NamiButton>
          <NamiButton
            v-if="!user || user.id !== authUser?.profile.sub"
            class="relative"
            :class="isFollowing ? 'w-[104px]' : 'w-24'"
            :buttonType="isFollowing ? 'secondary' : 'primary'"
            @click="toggleFollow"
            :disabled="
              authUser?.profile.sub === user?.id ||
              initialFollowPending ||
              !user ||
              loading
            "
          >
            {{ isFollowing ? "Unfollow" : "Follow" }}
          </NamiButton>
          <NamiButton
            v-if="isLoggedUser && user?.id === authUser?.profile.sub"
            button-type="secondary"
            text
            :icon="IconPencil"
            :href="`/${mappedLocale}/settings/profile`"
          />
          <ContextMenuList
            closeOnOutsideClick
            defaultPosition="bottom-left"
            :options="[
              {
                name: 'Report',
                action: () => {
                  if (!user) appStore?.openLoginRequiredModal();
                  else showReportModal = true;
                },
                icon: IconFlag,
              },
              {
                name: isUserBlocked ? 'Unblock' : 'Block',
                action: () => {
                  isUserBlocked ? unblockUser() : blockUser();
                },
                icon: IconForbid,
              },
            ]"
          >
            <template v-slot="{ isOpen, open, close }">
              <NamiButton
                @buttonClick="isOpen ? close() : open()"
                buttonType="secondary"
                :icon="IconDots"
                :text="!isOpen"
                iconPosition="right"
                :disabled="loading || !user"
                noWaves
              />
            </template>
          </ContextMenuList>
        </div>
        <div class="header-grid-username inline-flex items-center">
          <template v-if="loading">
            <Skeleton class="h-8 max-w-128 w-full" />
          </template>
          <template v-else-if="user">
            <BadgeUsername :badges :official :verified>
              {{ displayName }}
            </BadgeUsername>
          </template>
        </div>
        <div class="header-grid-info text-sm sm:text-base space-y-2">
          <template v-if="loading">
            <Skeleton class="h-4 w-32 mt-2 mb-1" />
            <Skeleton class="h-20" />
          </template>
          <template v-else-if="user">
            <div class="space-x-2">
              <span class="truncate font-normal opacity-70">
                @{{ username }}
              </span>
              <span
                class="ml-2 text-sm my-auto font-normal bg-neutral-300 dark:bg-neutral-800 px-2 rounded-md opacity-70 select-none"
                v-if="followsUser"
                >Follows You</span
              >
            </div>
            <div class="space-x-2">
              <TheNuxtLink
                :to="href.user.username.following(username)"
                class="hover:underline"
              >
                <i18n-t
                  keypath="components.UserProfileHeader.label.following"
                  :plural="data?.following ?? 0"
                >
                  <template #count>
                    <span class="font-medium">{{
                      formatNumber(data?.following ?? 0)
                    }}</span>
                  </template>
                </i18n-t>
              </TheNuxtLink>
              <TheNuxtLink
                :to="href.user.username.followers(username)"
                class="hover:underline"
              >
                <i18n-t
                  keypath="components.UserProfileHeader.label.followers"
                  :plural="data?.followers ?? 0"
                >
                  <template #count>
                    <span class="font-medium">{{
                      formatNumber(data?.followers ?? 0)
                    }}</span>
                  </template>
                </i18n-t>
              </TheNuxtLink>
            </div>
            <div>
              <TheNuxtLink
                v-if="link"
                :href="
                  link.indexOf(config.public.baseUrl) === 0
                    ? link
                    : `https://link.namicomi.com/?url=${link}`
                "
                :target="
                  link.indexOf(config.public.baseUrl) === 0 ? '_self' : '_blank'
                "
                :title="link"
                class="inline-flex items-center space-x-2 mr-4"
                rel="nooponer noreferrer nofollow"
              >
                <IconLink :size="20" />
                <span class="hover:underline text-nami-comi-blue">
                  {{
                    truncate(
                      link
                        .replace(/^https?:\/\/(www\.)?/, "")
                        .replace(/\/$/, ""),
                      24,
                    )
                  }}
                </span>
              </TheNuxtLink>
              <div
                v-if="location"
                class="inline-flex items-center space-x-2 mr-4"
              >
                <IconMapPin :size="20" />
                <!-- Because TablerIcon's IconMapPin is bugged -->
                <span>{{ location }}</span>
              </div>
              <div
                v-if="pronouns"
                class="inline-flex items-center space-x-2 mr-4"
              >
                <IconUser :size="20" />
                <span>{{ pronouns }}</span>
              </div>
              <div v-if="age" class="inline-flex items-center space-x-2 mr-4">
                <IconBalloon :size="20" />
                <span>{{ age }}</span>
              </div>
              <div v-if="createdAt" class="inline-flex items-center space-x-2">
                <IconCalendar :size="20" />
                <span>Joined {{ createdAt }}</span>
              </div>
            </div>
          </template>
        </div>
        <div v-if="loading || about" class="header-grid-description">
          <Skeleton v-if="loading" class="h-12" />
          <div
            v-else-if="about"
            class="break-words text-sm sm:text-base"
            style="word-break: break-word"
          >
            <!-- <MarkdownRender :text="about" /> -->
            {{ about }}
          </div>
        </div>
        <!-- x followers / y following would go here -->
      </div>
    </div>

    <div class="sm:px-4 lg:px-8 relative">
      <div class="container flex">
        <slot name="tabs"></slot>
      </div>
    </div>
  </div>
  <ReportModal
    v-if="user"
    v-model="showReportModal"
    entityType="user"
    :entityId="user.id"
  />
</template>

<script setup lang="ts">
import {
  IconBalloon,
  IconCalendar,
  IconDots,
  IconFlag,
  IconLink,
  IconMapPin,
  IconPencil,
  IconUser,
  IconMail,
  IconForbid,
} from "@tabler/icons-vue";
import { parseUserForDisplay } from "~/utils/display/user";
import {
  User,
  Block,
  type UserEntity,
  isFrame,
  isUserProfile,
  type UserProfile,
  Organization,
} from "~/src/api";
import { parseProfileForDisplay } from "~/utils/display/userProfile";
import { getUserFollowStats } from "~/composables/feed/getUserFollowStats";
import { injectTwilioContext } from "../app/context/sources/twilio/context";
import BadgeUsername from "~/components/badge/BadgeUsername.vue";

interface Props {
  username: string;
  user?: UserEntity | null;
  loading?: boolean;
}

const props = defineProps<Props>();

const targetUsersForConversationCreation =
  useTargetParticipantsForNewConversation();
const { primaryTwilioClient } = injectTwilioContext();
const { conversations } = primaryTwilioClient;
const href = useHref();
const nuxtApp = useNuxtApp();
const mappedLocale = computed(() => reverseLocaleMap(useI18n().locale.value));
const config = useRuntimeConfig();
const authStore = nuxtApp.$auth();
const appStore = nuxtApp.$app();
const isLoggedIn = useIsLoggedIn();
const authUser = computed(() => authStore?.user);
const authUserId = computed(() => authUser.value?.profile.sub);
const isLoggedUser = computed(
  () =>
    (props.user && authUser.value?.profile.sub === props.user.id) ||
    hasAdminAccess(authUser.value),
);

const showReportModal = ref(false);

const { isFollowing, toggleFollow, initialFollowPending } = useFollow(
  "user",
  computed(() => props.user?.id ?? ""),
);

const userProfile = computed(() => {
  const profile = props.user?.relationships.find(isUserProfile);
  if (profile?.attributes)
    return {
      ...profile,
      relationships: [],
    } as UserProfile;
});

const { data, error } = useAsyncData(
  `user-header-data-${props.username}`,
  async () => getUserFollowStats(props.user?.id),
  { watch: [() => props.user?.id] },
);

const { data: followsUser } = useAsyncData(
  `${props.username}-follows-user`,
  async () => {
    if (!authUserId.value || !props.user) return false;
    if (authUserId.value === props.user.id) return false;
    const follows = await User.getFollows({
      originId: props.user.id,
      targetId: authUserId.value,
      limit: 1,
    });

    return follows.data.length >= 1;
  },
  { watch: [() => props.user?.id, authUserId], server: false },
);

const { data: isUserBlocked } = useAsyncData(
  `is-user-blocked-${props.username}`,
  async () => {
    if (!isLoggedIn.value || !props.user || authUserId.value === props.user.id)
      return false;

    const list = await Block.listBlocks(
      {
        userId: authUserId.value,
        targetUserIds: [props.user.id],
      },
      await getTokenOrThrow(),
    );

    return list.meta.total > 0;
  },
  { watch: [() => props.user?.id, isLoggedIn] },
);

const refs = toRefs(props);
const { banner, displayName, username, createdAt, verified, official, badges } =
  parseUserForDisplay(refs.user!);
const { pronouns, age, location, link, about } =
  parseProfileForDisplay(userProfile);

const formatter = new Intl.NumberFormat("en");
const formatNumber = (v: number) => formatter.format(v);

async function goToSendMessage() {
  assertDefined(props.user);

  for (const conversation of conversations.value) {
    if (conversation.attributes.private === "false") continue;
    const participants = await conversation.getParticipants();
    if (participants.some((p) => p.identity === props.user!.id)) {
      return await navigateTo(href.value.messages.id(conversation.sid));
    }
  }

  targetUsersForConversationCreation.value = [props.user];
  await navigateTo(href.value.messages.id("_"));
}

async function blockUser() {
  await executeWithNotificationOnError(async () => {
    if ((await confirmBlockUser()) !== "confirm") return;
    if (!props.user?.id) return;
    await Block.createBlock(props.user.id, await getTokenOrThrow());
    appStore?.notify({
      preset: "success.plain",
      detail: "User has been blocked",
      timer: 3000,
    });
    isUserBlocked.value = true;
  });
}

async function unblockUser() {
  await executeWithNotificationOnError(async () => {
    if ((await confirmUnblockUser()) !== "confirm") return;
    if (!props.user?.id) return;
    await Block.removeBlock(props.user.id, await getTokenOrThrow());
    appStore?.notify({
      preset: "success.plain",
      detail: "User has been unblocked",
      timer: 3000,
    });
    isUserBlocked.value = false;
  });
}

async function confirmBlockUser() {
  return await appStore?.prompt("Are you sure you want to block this user?", {
    icon: "confused",
    buttons: {
      cancel: {
        buttonType: "secondary",
        buttonText: "Cancel",
      },
      confirm: {
        buttonType: "danger",
        buttonText: "Block",
      },
    },
  });
}

async function confirmUnblockUser() {
  return await appStore?.prompt("Are you sure you want to unblock this user?", {
    icon: "confused",
    buttons: {
      cancel: {
        buttonType: "secondary",
        buttonText: "Cancel",
      },
      confirm: {
        buttonType: "primary",
        buttonText: "Unblock",
      },
    },
  });
}
</script>
